import React, { useCallback, useState } from "react";

import { graphql, useMutation } from "react-relay";
import { useAlert } from "../../common/Alert";
import { IntegrationBokaklippCreateScreenMutation } from "./__generated__/IntegrationBokaklippCreateScreenMutation.graphql";
import { IntegrationLogo } from "./components/Logos/Logos";
import { IntegrationActivateScreen } from "./components/IntegrationActivation";
import { FormattedMessage } from "react-intl";
import { IntegrationBokaklippDeleteMutation } from "./__generated__/IntegrationBokaklippDeleteMutation.graphql";
import { IntegrationBokaklippEditUpdateMutation } from "./__generated__/IntegrationBokaklippEditUpdateMutation.graphql";
import { IntegrationSettings } from "./components/IntegrationSettings";

export function IntegrationBokaklipp(props: {
  mode: "create" | "edit";
  onCompleted: () => void;
}) {
  switch (props.mode) {
    case "create":
      return <BokaklippCreate onCompleted={props.onCompleted} />;
    case "edit":
      return <BokaKlippEdit onCompleted={props.onCompleted} />;
    default:
      return null;
  }
}

function BokaklippCreate({ onCompleted }: { onCompleted: () => void }) {
  const [token, setToken] = React.useState("");

  const isValid = token.trim().length > 10;

  const [create, loading] =
    useMutation<IntegrationBokaklippCreateScreenMutation>(graphql`
      mutation IntegrationBokaklippCreateScreenMutation($token: String!) {
        store {
          activateIntegrationBokaklipp(token: $token) {
            id
          }
        }
      }
    `);

  const Alert = useAlert();

  const onCreate = useCallback(() => {
    if (!token) return;

    create({
      onCompleted: (d) => {
        if (d.store.activateIntegrationBokaklipp) {
          onCompleted();
        } else {
          Alert.error(
            "Fel Uppstod",
            "Kunde inte skapa integrationen, kontrollera att du har knappat in rätt token."
          );
        }
      },
      variables: { token: token.trim() },
    });
  }, [Alert, create, onCompleted, token]);

  return (
    <IntegrationActivateScreen
      header={
        <IntegrationActivateScreen.Header
          logo={<IntegrationLogo type="bokaklipp" />}
        />
      }
      activation={
        <IntegrationActivateScreen.Input
          value={token}
          onChangeText={setToken}
          type="api-key"
        />
      }
      onActivate={onCreate}
      isValid={isValid}
      isActivating={loading}
    />
  );
}

function BokaKlippEdit({ onCompleted }: { onCompleted: () => void }) {
  const [token, setToken] = useState("");
  const isValid = token.trim().length > 10;

  const [update, updateLoading] =
    useMutation<IntegrationBokaklippEditUpdateMutation>(graphql`
      mutation IntegrationBokaklippEditUpdateMutation($token: String!) {
        store {
          activateIntegrationBokaklipp(token: $token) {
            id
          }
        }
      }
    `);
  const Alert = useAlert();
  const onUpdate = useCallback(() => {
    update({
      onError: () => {
        Alert.alert(
          "Fel Uppstod",
          "Kunde inte uppdatera integrationen, kontrollera att du har knappat in rätt länk "
        );
      },
      onCompleted: (d) => {
        if (d.store.activateIntegrationBokaklipp?.id) {
          onCompleted();
        } else {
          Alert.error(
            "Fel Uppstod",
            "Kunde inte uppdatera integrationen, kontrollera att du har knappat in rätt länk "
          );
        }
      },
      variables: {
        token: token.trim(),
      },
    });
  }, [update, token, Alert, onCompleted]);

  const [deleteIntegration, pending] =
    useMutation<IntegrationBokaklippDeleteMutation>(graphql`
      mutation IntegrationBokaklippDeleteMutation {
        store {
          deleteIntegrationBokaklipp
        }
      }
    `);

  const requestDelete = () => {
    deleteIntegration({
      variables: {},
      onCompleted,
    });
  };

  return (
    <IntegrationSettings
      header={
        <IntegrationActivateScreen.Header
          logo={<IntegrationLogo type="bokaklipp" />}
        />
      }
      delete={{
        pending,
        requestDelete,
      }}
      save={{
        requestSave: onUpdate,
        pending: updateLoading,
        disabled: updateLoading || !isValid,
      }}
      body={
        <IntegrationSettings.Input
          value={token}
          onChangeText={setToken}
          type="api-key"
          label={
            <FormattedMessage
              description="IntegrationBokaklippScreen - Update - Title"
              defaultMessage="Update token"
            />
          }
        />
      }
    />
  );
}
